import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CheckIcon,
	MapPinIcon,
} from '@heroicons/react/24/outline';
import { getServiceArea, serviceAreas, services } from './data';
import SunRaysHero from './SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

export default function ServiceAreaDetail() {
	const { areaId } = useParams();
	const area = getServiceArea(areaId);

	if (!area)
		return (
			<Navigate
				to='/service-areas'
				replace
			/>
		);

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={3} />
				<div className='mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:px-8'>
					<Link
						to='/service-areas'
						className='inline-flex items-center gap-2 text-sm font-semibold text-ink-500 hover:text-ink-900'
					>
						<ArrowLeftIcon className='h-4 w-4' />
						Service areas
					</Link>
					<div className='mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end'>
						<div className='lg:col-span-8'>
							<Eyebrow>Local solar service</Eyebrow>
							<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05]'>
								{area.label}
							</h1>
							<p className='mt-6 max-w-2xl text-lg leading-8 text-ink-600'>
								{area.summary}
							</p>
						</div>
						<div className='lg:col-span-4'>
							<div className='rounded-2xl bg-white/70 p-6 ring-1 ring-ink-100 backdrop-blur-sm'>
								<MapPinIcon className='h-9 w-9 text-primary-600' />
								<p className='mt-5 text-sm leading-6 text-ink-600'>
									Serving homeowners, businesses, and property owners across
									{` ${area.name}`}.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-12 lg:grid-cols-12'>
						<div className='lg:col-span-7'>
							<Eyebrow>{area.name}</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
								What matters locally.
							</h2>
							<div className='mt-6 space-y-5 text-base leading-7 text-ink-500'>
								{area.details.map((detail) => (
									<p key={detail}>{detail}</p>
								))}
							</div>
							<div className='mt-10 flex flex-wrap gap-2'>
								{area.markets.map((market) => (
									<span
										key={market}
										className='rounded-full bg-surface px-4 py-2 text-sm font-medium text-ink-700 ring-1 ring-ink-100'
									>
										{market}
									</span>
								))}
							</div>
						</div>
						<aside className='lg:col-span-5'>
							<div className='sg-card p-8'>
								<h3 className='font-display text-2xl font-medium text-ink-900'>
									Local considerations
								</h3>
								<ul className='mt-6 space-y-4'>
									{area.considerations.map((item) => (
										<li
											key={item}
											className='flex items-start gap-3'
										>
											<div className='mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100'>
												<CheckIcon
													className='h-3 w-3 text-primary-700'
													strokeWidth={3}
												/>
											</div>
											<span className='text-sm leading-6 text-ink-700'>
												{item}
											</span>
										</li>
									))}
								</ul>
								<Link
									to='/contact'
									className='mt-8 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
								>
									Get local solar guidance
									<ArrowRightIcon className='h-4 w-4' />
								</Link>
							</div>
						</aside>
					</div>
				</div>
			</section>

			{area.benefits?.length > 0 && (
				<section className='bg-white pb-20 sm:pb-28'>
					<div className='mx-auto max-w-7xl px-6 lg:px-8'>
						<Eyebrow>Why {area.name}</Eyebrow>
						<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
							Solar incentives in {area.name}.
						</h2>
						<div className='mt-10 grid grid-cols-1 gap-6 md:grid-cols-2'>
							{area.benefits.map((benefit) => (
								<div
									key={benefit.title}
									className='sg-card p-7'
								>
									<h3 className='font-display text-xl font-medium text-ink-900'>
										{benefit.title}
									</h3>
									<p className='mt-3 text-base leading-7 text-ink-500'>
										{benefit.description}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>
			)}

			<section className='bg-surface py-20 sm:py-28 border-y border-ink-100'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<Eyebrow>Available services</Eyebrow>
					<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
						Solar services in {area.name}.
					</h2>
					<div className='mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
						{services.map((service) => (
							<Link
								key={service.id}
								to={`/services/${service.id}`}
								className='group sg-card sg-card-interactive p-6'
							>
								<service.icon className='h-8 w-8 text-primary-600' />
								<h3 className='font-display mt-5 text-xl font-medium text-ink-900'>
									{service.title}
								</h3>
								<p className='mt-3 text-sm leading-6 text-ink-500'>
									{service.summary}
								</p>
							</Link>
						))}
					</div>
				</div>
			</section>

			<section className='bg-white py-20'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900'>
						Other service areas
					</h2>
					<div className='mt-8 grid grid-cols-1 gap-4 md:grid-cols-3'>
						{serviceAreas
							.filter((item) => item.id !== area.id)
							.map((item) => (
								<Link
									key={item.id}
									to={`/service-areas/${item.id}`}
									className='group sg-card sg-card-interactive p-6'
								>
									<h3 className='font-display text-xl font-medium text-ink-900'>
										{item.name}
									</h3>
									<p className='mt-3 text-sm leading-6 text-ink-500'>
										{item.summary}
									</p>
								</Link>
							))}
					</div>
				</div>
			</section>
		</div>
	);
}
